import { useState, useMemo, useEffect, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableCollectionBinding, useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
  virtual_tour_type?: string | null;
  virtual_tour_thumbnail?: string | null;
}

type TabKey = 'community' | 'apartment';

const TAB_LABELS: Record<TabKey, string> = {
  community: 'Tour Your New Community',
  apartment: 'Tour Your New Home',
};

/**
 * Returns true only if `value` looks like a real URL/path. Upstream feeds
 * sometimes pass literal strings like "false", "False", "null", "none",
 * etc. instead of an empty value — those should be treated as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (
    lower === 'false' ||
    lower === 'true' ||
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'none' ||
    lower === 'n/a'
  ) {
    return false;
  }
  return true;
}

function classifyTour(tour: VirtualTour): TabKey {
  const typeVal = (tour.virtual_tour_type || '').toLowerCase();
  if (typeVal.includes('apartment')) return 'apartment';
  if (typeVal.includes('community')) return 'community';

  const title = (tour.virtual_tour_title || '').toLowerCase();
  if (title.includes('apartment') || title.includes('unit') || title.includes('home') || title.includes('floorplan'))
    return 'apartment';
  return 'community';
}

/** Parse square footage from a tour title when present (e.g. "A2 — 850 Sq Ft"). */
function parseSqftFromTitle(title: string | null | undefined): number | null {
  if (!title) return null;
  const match =
    title.match(/(\d[\d,]*)\s*(?:sq\.?\s*ft\.?|sqft|s\.?f\.?)\b/i) ??
    title.match(/\b(?:sq\.?\s*ft\.?|sqft|s\.?f\.?)\s*[:=]?\s*(\d[\d,]*)/i);
  if (!match) return null;
  const value = Number(match[1].replace(/,/g, ''));
  return Number.isFinite(value) ? value : null;
}

const BEDROOM_WORD_TO_COUNT: Record<string, number> = {
  studio: 0,
  std: 0,
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
};

/** Parse bedroom count from a tour title when present (Studio = 0). */
function parseBedroomsFromTitle(title: string | null | undefined): number | null {
  if (!title) return null;
  const lower = title.toLowerCase();

  if (/\bstudio\b|\bstd\b/.test(lower)) return 0;

  const numeric =
    lower.match(/(\d+)\s*(?:bed(?:room)?s?|br)\b/) ??
    lower.match(/\b(?:bed(?:room)?s?|br)\s*[:=]?\s*(\d+)/);
  if (numeric) {
    const value = Number(numeric[1]);
    return Number.isFinite(value) ? value : null;
  }

  // e.g. "2x1", "2X1.5" — beds x baths naming common in apartment tours
  const bedsByBaths = lower.match(/\b(\d+)\s*[x×]\s*\d/);
  if (bedsByBaths) {
    const value = Number(bedsByBaths[1]);
    return Number.isFinite(value) ? value : null;
  }

  const word = lower.match(/\b(zero|one|two|three|four|five|six)\s*(?:bed(?:room)?s?|br)\b/);
  if (word) return BEDROOM_WORD_TO_COUNT[word[1]] ?? null;

  return null;
}

/**
 * Sort apartment tours: sqft smallest → largest when present;
 * otherwise (and as a tiebreaker) by bedroom count smallest → largest.
 * Tours with neither stay last, preserving relative order.
 */
function sortApartmentTours(tours: VirtualTour[]): VirtualTour[] {
  return [...tours].sort((a, b) => {
    const aSqft = parseSqftFromTitle(a.virtual_tour_title);
    const bSqft = parseSqftFromTitle(b.virtual_tour_title);
    const aBeds = parseBedroomsFromTitle(a.virtual_tour_title);
    const bBeds = parseBedroomsFromTitle(b.virtual_tour_title);

    if (aSqft != null && bSqft != null && aSqft !== bSqft) return aSqft - bSqft;
    if (aSqft != null && bSqft == null) return -1;
    if (aSqft == null && bSqft != null) return 1;

    if (aBeds != null && bBeds != null && aBeds !== bBeds) return aBeds - bBeds;
    if (aBeds != null && bBeds == null) return -1;
    if (aBeds == null && bBeds != null) return 1;

    return 0;
  });
}

function TourRow({ tour, onOpen }: { tour: VirtualTour; onOpen: (tour: VirtualTour) => void }) {
  const hasThumbnail = isUsableUrl(tour.virtual_tour_thumbnail);

  return (
    <div className="vt-row">
      {hasThumbnail && (
        <div className="vt-row-thumb">
          <VibesitesImage
            url={tour.virtual_tour_thumbnail!}
            alt={tour.virtual_tour_title || 'Virtual Tour'}
            sizes="(min-width: 640px) 140px, 88px"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="vt-row-body">
        <div className="vt-row-text">
          <p className="font-body text-base sm:text-lg text-[var(--vibe-color-text)]">
            {tour.virtual_tour_title || 'Virtual Tour'}
          </p>
          <p className="font-body text-xs text-[var(--vibe-color-text-muted)] mt-1">Virtual Tour</p>
        </div>

        <button
          type="button"
          onClick={() => onOpen(tour)}
          className="vt-view-btn group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
        >
          <span className="relative z-10">View Virtual Tour</span>
          <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function TourModal({ tour, onClose }: { tour: VirtualTour; onClose: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="vt-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={tour.virtual_tour_title || 'Virtual Tour'}
    >
      <div className="vt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vt-modal-header">
          <p className="font-body text-sm sm:text-base text-white truncate pr-4">
            {tour.virtual_tour_title || 'Virtual Tour'}
          </p>
          <button type="button" onClick={onClose} className="vt-modal-close" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="vt-modal-body">
          <iframe
            src={tour.virtual_tour_url || ''}
            className="w-full h-full border-0"
            allowFullScreen
            title={tour.virtual_tour_title || 'Virtual Tour'}
          />
        </div>
      </div>
    </div>
  );
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const validTours = useMemo(
    () => (tours || []).filter((t) => isUsableUrl(t.virtual_tour_url)),
    [tours],
  );

  const grouped = useMemo(() => {
    const map: Record<TabKey, VirtualTour[]> = { community: [], apartment: [] };
    for (const tour of validTours) {
      map[classifyTour(tour)].push(tour);
    }
    map.apartment = sortApartmentTours(map.apartment);
    return map;
  }, [validTours]);

  const availableTabs = useMemo(
    () => (['community', 'apartment'] as TabKey[]).filter((k) => grouped[k].length > 0),
    [grouped],
  );

  const [activeTab, setActiveTab] = useState<TabKey>('community');
  const [modalTour, setModalTour] = useState<VirtualTour | null>(null);

  const openModal = useCallback((tour: VirtualTour) => setModalTour(tour), []);
  const closeModal = useCallback(() => setModalTour(null), []);

  const effectiveTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0] || 'community';
  const activeTours = grouped[effectiveTab] || [];

  return (
    <section className="bg-white py-8 md:py-12">
      <EditableCollectionBinding bindingPath="virtualtourembed.tours" label="Virtual Tours">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {validTours.length === 0 ? (
            <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
              <p className="text-[var(--vibe-color-text-muted)] font-body">
                No virtual tours are available for this property.
              </p>
            </div>
          ) : (
            <>
              {/* Tabs — only shown when both Community and Apartment tours exist */}
              {availableTabs.length > 1 && (
                <div className="vt-tab-row" role="tablist">
                  {availableTabs.map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={effectiveTab === key}
                      onClick={() => setActiveTab(key)}
                      className={`vt-tab ${effectiveTab === key ? 'vt-tab--active' : ''}`}
                    >
                      {TAB_LABELS[key]}
                    </button>
                  ))}
                </div>
              )}

              <div className={`vt-list ${availableTabs.length <= 1 ? 'vt-list--no-tabs' : ''}`}>
                {activeTours.map((tour, i) => (
                  <TourRow key={`${effectiveTab}-${i}`} tour={tour} onOpen={openModal} />
                ))}
              </div>
            </>
          )}
        </div>
      </EditableCollectionBinding>

      {modalTour && <TourModal tour={modalTour} onClose={closeModal} />}

      <style>{`
        /* ── Tab bar ── */
        .vt-tab-row {
          display: flex;
          gap: 0;
          width: 100%;
          max-width: 100%;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          margin-bottom: 0;
        }
        .vt-tab {
          flex: 1 1 0;
          min-width: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          padding: 0.75rem 1.25rem;
          color: var(--vibe-color-text-muted, #6b7280);
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease;
          text-align: center;
          white-space: nowrap;
        }
        .vt-tab:hover {
          color: var(--vibe-color-text, #111);
        }
        .vt-tab--active {
          color: var(--vibe-color-text, #111);
          border-bottom-color: var(--vibe-color-text, #111);
          font-weight: 600;
        }
        @media (max-width: 639px) {
          .vt-tab {
            white-space: normal;
            line-height: 1.25;
            padding: 0.75rem 0.5rem;
            font-size: 0.8125rem;
          }
        }

        /* ── Tour list ── */
        .vt-list {
          display: flex;
          flex-direction: column;
        }
        .vt-list--no-tabs {
          border-top: 1px solid var(--vibe-color-border, #e5e7eb);
        }

        /* ── Tour row ── */
        .vt-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem 0;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        .vt-row:last-child {
          border-bottom: none;
        }
        .vt-row-body {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .vt-row-text {
          flex: 1;
          min-width: 0;
          padding: 0.5rem 0;
        }

        @media (min-width: 640px) {
          .vt-row { gap: 1.5rem; }
          .vt-row-body { gap: 1.5rem; }
        }

        /* On mobile, stack title above the CTA so titles aren't
           crushed between the thumbnail and the nowrap button. */
        @media (max-width: 639px) {
          .vt-row {
            align-items: flex-start;
            gap: 0.75rem;
          }
          .vt-row-body {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
          .vt-row-text {
            padding: 0;
          }
        }

        /* ── Thumbnail ── */
        .vt-row-thumb {
          flex-shrink: 0;
          width: 140px;
          height: 90px;
          border-radius: 4px;
          overflow: hidden;
          background: var(--vibe-color-surface, #f5f5f5);
        }
        @media (max-width: 639px) {
          .vt-row-thumb {
            width: 88px;
            height: 60px;
            margin-top: 0.125rem;
          }
        }

        /* ── View button ── */
        .vt-view-btn {
          flex-shrink: 0;
          justify-content: center;
          white-space: nowrap;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 9999px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .vt-view-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .vt-view-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .vt-view-btn:hover::before {
          left: 100%;
        }
        .vt-view-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }

        /* ── Modal ── */
        .vt-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.75);
          animation: vtFadeIn 0.2s ease;
        }
        .vt-modal {
          position: relative;
          width: 92vw;
          max-width: 1100px;
          background: #111;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: vtSlideUp 0.25s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        .vt-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #111;
          flex-shrink: 0;
        }
        .vt-modal-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .vt-modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }
        .vt-modal-body {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
        }
        .vt-modal-body iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }
        @media (max-width: 639px) {
          .vt-modal {
            width: 100vw;
            max-width: none;
            border-radius: 0;
          }
        }

        @keyframes vtFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes vtSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourEmbed;
